import { DocumentDownloadOptions, DownloadFormat, Fonts, TextAlignment } from '../../../../../backend/src/types/types';
import { FilterField } from '@/api/queries/useGetMetadataFilterOptions';

export type DownloadKind = 'written' | 'table' | 'list' | 'zip' | 'image';
export type ImageFormat = 'png' | 'svg';
export type AnyFormat = DownloadFormat | ImageFormat | 'zip';

export interface ColumnsSpec {
  available: string[];
  default: string[];
  relativity?: FilterField[];
}

export interface DownloadFormValues<TFormat extends AnyFormat = AnyFormat> {
  filename: string;
  format: TFormat;
  scope?: string;
  columns?: string[];
  relativityColumns?: string[];
  style?: string;
  docOptions?: DocumentDownloadOptions;
  includeSourceDocuments?: boolean;
  orderByDate?: boolean;
}

interface BaseVariant<TFormat extends AnyFormat = AnyFormat> {
  id: string;
  kind: DownloadKind;
  label: string;
  description?: string;
  icon?: string;
  formats: readonly TFormat[];
  defaultFilename?: string;
  columns?: ColumnsSpec;
  style?: boolean;
  docOptions?: boolean;
  sourceDocs?: boolean;
  largeExportWarning?: boolean;
}

export type AsyncVariant<TFormat extends AnyFormat = AnyFormat> = BaseVariant<TFormat> & {
  mode: 'async';
  mutate(values: DownloadFormValues<TFormat>): Promise<{ exportId: string; jobId?: string }>;
  /** SSE endpoint base URL (exportId appended as ?exportId= by the progress modal). */
  progressEndpoint(exportId: string): string;
  /**
   * Optional REST endpoint URL builder (exportId appended as `?exportId=`)
   * returning the persisted terminal status. The progress modal polls it when
   * the SSE channel drops. Sibling to `progressEndpoint` and gated by the same
   * per-resource download permission.
   */
  statusEndpoint?(exportId: string): string;
  /** Called when the user clicks "Download" on the ready progress modal. */
  downloadReady(exportId: string, format: TFormat | undefined): Promise<void>;
  readyDescription?: string;
  downloadButtonText?: string;
};

export type SyncVariant<TFormat extends AnyFormat = AnyFormat> = BaseVariant<TFormat> & {
  mode: 'sync';
  mutate(values: DownloadFormValues<TFormat>): Promise<void>;
};

export type DownloadVariant<TFormat extends AnyFormat = AnyFormat> = AsyncVariant<TFormat> | SyncVariant<TFormat>;

export interface DownloadScope {
  id: string;
  label: string;
  totalCount?: number;
}

export interface DocumentDownloadScopePreset {
  type: 'documents';
  selectedCount: number;
  hasActiveFilters: boolean;
}

export interface DownloadDefinition {
  title?: string;
  scopes?: DownloadScope[];
  scopePreset?: DocumentDownloadScopePreset;
  defaultScopeId?: string;
  variants: DownloadVariant[];
  defaultVariantId?: string;
  totalCount?: number;
}

export interface ResolvedDownloadScopes {
  scopes?: DownloadScope[];
  defaultScopeId?: string;
}

export const resolveDownloadScopes = (definition: DownloadDefinition): ResolvedDownloadScopes => {
  if (definition.scopes) {
    return {
      scopes: definition.scopes,
      defaultScopeId: definition.defaultScopeId,
    };
  }

  if (definition.scopePreset?.type === 'documents') {
    const scopes: DownloadScope[] = [];
    if (definition.scopePreset.selectedCount > 0) {
      scopes.push({
        id: 'selected',
        label: `Download ${definition.scopePreset.selectedCount} selected document${
          definition.scopePreset.selectedCount !== 1 ? 's' : ''
        }`,
        totalCount: definition.scopePreset.selectedCount,
      });
    }
    if (definition.scopePreset.hasActiveFilters) {
      scopes.push({ id: 'filtered', label: 'Download only filtered documents' });
    }
    scopes.push({ id: 'all', label: 'Download all documents' });

    return {
      scopes,
      defaultScopeId:
        definition.scopePreset.selectedCount > 0
          ? 'selected'
          : definition.scopePreset.hasActiveFilters
            ? 'filtered'
            : 'all',
    };
  }

  return {
    scopes: definition.scopes,
    defaultScopeId: definition.defaultScopeId,
  };
};

export const defaultDocOptions = (): DocumentDownloadOptions => ({
  fontFamily: Fonts.TimesNewRoman,
  textAlignment: TextAlignment.Left,
  includeReferences: true,
});

export const kindShowsFilename = (kind: DownloadKind): boolean => kind !== 'image';

export const defaultIconForKind = (kind: DownloadKind): string => {
  switch (kind) {
    case 'table':
      return 'lucide--table';
    case 'written':
      return 'lucide--file-text';
    case 'list':
      return 'lucide--list';
    case 'zip':
      return 'lucide--folder-archive';
    case 'image':
      return 'lucide--image';
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
};
